"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MapPin, Phone, Mail, Clock, Loader, CalendarHeart } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  address: "ՀՀ, ք. Երևան, Աբովյան 1/3",
  phone: "+374 10 123456",
  email: "info@geocosmos.am",
  hours: "Երկուշաբթի - Ուրբաթ: 09:00 - 18:00"
};
function EventInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <CalendarHeart className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground">
          Հարցումն ուղարկված է
        </h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary rounded-md">
          Ուղարկել նոր հարցում
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Անունը պարտադիր է"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Անուն Ազգանուն</FormLabel>
              <FormControl>
                <Input placeholder="Հովհաննես Հովհաննիսյան" {...field} className="bg-transparent border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Էլ. փոստը պարտադիր է",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Անվավեր էլ. փոստ"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Էլեկտրոնային փոստ</FormLabel>
              <FormControl>
                <Input type="email" placeholder="example@geocosmos.am" {...field} className="bg-transparent border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Հեռախոսահամարը պարտադիր է",
        pattern: {
          value: /^[0-9+\-\s()]+$/,
          message: "Անվավեր հեռախոսահամար"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Հեռախոսահամար</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+374 00 000000" {...field} className="bg-transparent border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">
                Ներառեք երկրի կոդը:
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_date" rules={{
        required: "Ամսաթիվը պարտադիր է"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Նախընտրելի ամսաթիվ</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="bg-transparent border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">
                Ընտրեք խորհրդատվության օրը:
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="event_type" rules={{
        required: "Ընտրեք ծառայության տեսակը"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Ծառայության տեսակ</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-transparent border-0 border-b border-input focus:ring-0 focus:border-primary rounded-md">
                    <SelectValue placeholder="Ընտրեք տարբերակը" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  {["Արբանյակային նկարների ստացում", "Գեոդեզիական Աշխատանքներ", "Թվային Քարտեզների Ստեղծում", "Այլ"].map((option, index) => <SelectItem key={index} value={option} data-index={index}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="attendees" rules={{
        required: "Այս դաշտը պարտադիր է"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Օբյեկտների քանակ / Մակերես</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Օրինակ՝ 50 հա" {...field} className="bg-transparent border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Հաղորդագրություն կամ լրացուցիչ պահանջներ</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-md border-0 border-b border-input bg-transparent px-3 py-2 text-sm shadow-md placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary transition-colors resize-y" placeholder="Նկարագրեք Ձեր նախագիծը..." {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">
              Տրամադրեք լրացուցիչ տեղեկատվություն ավելի ճշգրիտ առաջարկի համար:
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-default font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[240px] bg-primary text-primary-foreground hover:opacity-90 transition-all font-default font-bold uppercase tracking-wider rounded-md" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Ուղարկվում է...
          </> : "Ուղարկել Հարցումը"}
      </Button>
    </Form>;
}
export default function TienesUnEvento() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}